// Core map state singleton with pub/sub pattern
// Manages walls, monster placements, lights, openings, covers, and game state

/**
 * @typedef {import('../types/jsdoc-types.js').MonsterPlacement} MonsterPlacement
 * @typedef {import('../types/jsdoc-types.js').Wall} Wall
 * @typedef {import('../types/jsdoc-types.js').Opening} Opening
 * @typedef {import('../types/jsdoc-types.js').Light} Light
 * @typedef {import('../types/jsdoc-types.js').Cover} Cover
 * @typedef {import('../types/jsdoc-types.js').PCStartingPosition} PCStartingPosition
 * @typedef {import('../types/jsdoc-types.js').TurnState} TurnState
 * @typedef {import('../types/jsdoc-types.js').Character} Character
 * @typedef {import('../types/jsdoc-types.js').VisibilityState} VisibilityState
 * @typedef {import('../types/jsdoc-types.js').GridPoint} GridPoint
 */

/**
 * @typedef {object} MapVisibility
 * @property {Array<{col: number, row: number, illumination: string, visibility?: string, personallyVisible?: boolean, perceivedViaDarkvision?: boolean, spectatorOnly?: boolean}>} gridCells
 * @property {Array<{id: string, points: GridPoint[], blocksVision: boolean, personallyVisible?: boolean}>} walls
 * @property {Array<{id: string, points: GridPoint[], type: string, isOpen: boolean, personallyVisible?: boolean}>} openings
 * @property {Array<{id: string, col: number, row: number, size: string, personallyVisible?: boolean, spectatorOnly?: boolean}>} monsterPlacements
 * @property {Array<{id: string, col: number, row: number, brightRadius: number, dimRadius: number, isOn: boolean, source?: string, personallyVisible?: boolean}>} lights
 */

/**
 * @typedef {object} Player
 * @property {string} id
 * @property {string} username
 * @property {string} connectionState
 * @property {string} role
 * @property {string} avatarUrl
 */

/**
 * @typedef {object} InvitedUser
 * @property {string} userId
 * @property {string} username
 * @property {string} [avatarUrl]
 */

/**
 * @typedef {object} CombatState
 * @property {boolean} isActive
 * @property {Array<object>} participants
 * @property {number|null} currentTurnIndex
 * @property {number} currentRound
 */

/**
 * @typedef {object} GameHook
 * @property {string} text
 * @property {string} [imageURL]
 * @property {string} [title]
 * @property {string} [buttonText]
 * @property {boolean} dismissed
 */

/**
 * @typedef {object} WaitingRoomSeat
 * @property {{col: number, row: number}} position
 * @property {string} [playerId]
 * @property {string} [characterId]
 * @property {boolean} [ready]
 * @property {number} [offsetX]
 * @property {number} [offsetY]
 */

/**
 * @typedef {object} WaitingRoom
 * @property {boolean} isActive
 * @property {string} tavernMapId
 * @property {Array<WaitingRoomSeat>} seats
 */

class MapStateManager {
  constructor() {
    /** @type {Set<() => void>} */
    this.listeners = new Set();

    // Map geometry
    /** @type {Array<Wall>} */
    this.walls = [];
    /** @type {Array<MonsterPlacement>} */
    this.monsterPlacements = [];
    /** @type {Array<Light>} */
    this.lights = [];
    /** @type {Array<Opening>} */
    this.openings = [];
    /** @type {Array<Cover>} */
    this.covers = [];
    /** @type {Array<PCStartingPosition>} */
    this.pcStartingPositions = [];

    // Map metadata
    /** @type {string|null} */
    this.mapId = null;
    /** @type {string|null} */
    this.imageUploadId = null;
    /** @type {string} */
    this.backgroundUrl = '';
    /** @type {number} */
    this.gridSize = 140;
    /** @type {number} */
    this.gridOffsetX = 0;
    /** @type {number} */
    this.gridOffsetY = 0;
    /** @type {string} */
    this.backgroundColor = '#1a1a1a';
    /** @type {number} */
    this.gridRows = 0;
    /** @type {number} */
    this.gridCols = 0;

    // Game state
    /** @type {Array<Player>} */
    this.gamePlayers = [];
    /** @type {Array<Character>} */
    this.playerCharacters = [];
    /** @type {Array<InvitedUser>} */
    this.invitedUsers = [];
    /** @type {MapVisibility|null} */
    this.mapVisibility = null;

    // Combat and turn state
    /** @type {CombatState} */
    this.combatState = {
      isActive: false,
      participants: [],
      currentTurnIndex: null,
      currentRound: 1,
    };
    /** @type {TurnState|null} */
    this.turnState = null;

    // Adventure state
    /** @type {GameHook|null} */
    this.hook = null;
    /** @type {string|null} */
    this.objective = null;
    /** @type {boolean} */
    this.adventureCompleted = false;
    /** @type {boolean} */
    this.partyWiped = false;

    /** @type {'none'|'bright'|'dim'} */
    this.globalIllumination = 'bright';

    // Waiting room state (multiplayer only)
    /** @type {WaitingRoom|null} */
    this.waitingRoom = null;

    /** RNG seed for deterministic replay; surfaced in the debug overlay. */
    /** @type {number|null} */
    this.seed = null;
  }

  /**
   * Subscribe to state changes
   * @param {() => void} fn - Callback function
   * @returns {() => void} Unsubscribe function
   */
  subscribe(fn) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /**
   * Notify all listeners of state change
   */
  notify() {
    this.listeners.forEach((fn) => {
      fn();
    });
  }

  /**
   * Set walls and notify
   * @param {Array<Wall>} walls
   */
  setWalls(walls) {
    this.walls = walls;
    this.notify();
  }

  /**
   * Set monster placements and notify
   * @param {Array<MonsterPlacement>} monsterPlacements
   */
  setMonsterPlacements(monsterPlacements) {
    this.monsterPlacements = monsterPlacements;
    this.notify();
  }

  /**
   * Update a single monster placement
   * @param {string} placementId
   * @param {Partial<MonsterPlacement>} updates
   */
  updateMonsterPlacement(placementId, updates) {
    const index = this.monsterPlacements.findIndex((p) => p.id === placementId);
    if (index !== -1) {
      const existing = this.monsterPlacements[index];
      if (!existing) throw new Error(`Expected monster placement at index ${index}`);
      this.monsterPlacements[index] = /** @type {MonsterPlacement} */ ({ ...existing, ...updates });
      this.notify();
    }
  }

  /**
   * Set lights and notify
   * @param {Array<Light>} lights
   */
  setLights(lights) {
    this.lights = lights;
    this.notify();
  }

  /**
   * Set openings and notify
   * @param {Array<Opening>} openings
   */
  setOpenings(openings) {
    this.openings = openings;
    this.notify();
  }

  /**
   * Set covers and notify
   * @param {Array<Cover>} covers
   */
  setCovers(covers) {
    this.covers = covers;
    this.notify();
  }

  /**
   * Set PC starting positions and notify
   * @param {Array<PCStartingPosition>} positions
   */
  setPCStartingPositions(positions) {
    this.pcStartingPositions = positions;
    this.notify();
  }

  /**
   * Set map metadata
   * @param {object} metadata
   * @param {string} [metadata.mapId]
   * @param {string} [metadata.imageUploadId]
   * @param {string} [metadata.backgroundUrl]
   * @param {number} [metadata.gridSize]
   * @param {number} [metadata.gridOffsetX]
   * @param {number} [metadata.gridOffsetY]
   * @param {string} [metadata.backgroundColor]
   * @param {number} [metadata.gridRows]
   * @param {number} [metadata.gridCols]
   */
  setMapMetadata(metadata) {
    if (metadata.mapId !== undefined) this.mapId = metadata.mapId;
    if (metadata.imageUploadId !== undefined) this.imageUploadId = metadata.imageUploadId;
    if (metadata.backgroundUrl !== undefined) this.backgroundUrl = metadata.backgroundUrl;
    if (metadata.gridSize !== undefined) this.gridSize = metadata.gridSize;
    if (metadata.gridOffsetX !== undefined) this.gridOffsetX = metadata.gridOffsetX;
    if (metadata.gridOffsetY !== undefined) this.gridOffsetY = metadata.gridOffsetY;
    if (metadata.backgroundColor !== undefined) this.backgroundColor = metadata.backgroundColor;
    if (metadata.gridRows !== undefined) this.gridRows = metadata.gridRows;
    if (metadata.gridCols !== undefined) this.gridCols = metadata.gridCols;
    this.notify();
  }

  /**
   * Set turn state and notify
   * @param {TurnState|null} turnState
   */
  setTurnState(turnState) {
    this.turnState = turnState;
    this.notify();
  }

  /**
   * Set combat state and notify
   * @param {CombatState} combatState
   */
  setCombatState(combatState) {
    this.combatState = combatState;
    this.notify();
  }

  /**
   * Set map visibility and notify
   * @param {MapVisibility|null} visibility
   */
  setMapVisibility(visibility) {
    this.mapVisibility = visibility;
    this.notify();
  }

  /**
   * Set player characters and notify
   * @param {Array<Character>} characters
   */
  setPlayerCharacters(characters) {
    this.playerCharacters = characters;
    this.notify();
  }

  /**
   * Set game players and notify
   * @param {Array<Player>} players
   */
  setGamePlayers(players) {
    this.gamePlayers = players;
    this.notify();
  }

  /**
   * Set game hook and notify
   * @param {GameHook|null} hook
   */
  setHook(hook) {
    this.hook = hook;
    this.notify();
  }

  /**
   * Set adventure completed status
   * @param {boolean} completed
   */
  setAdventureCompleted(completed) {
    this.adventureCompleted = completed;
    this.notify();
  }

  /**
   * Set adventure objective
   * @param {string|null} objective
   */
  setObjective(objective) {
    this.objective = objective;
    this.notify();
  }

  /**
   * Get monster placement by ID
   * @param {string} placementId
   * @returns {MonsterPlacement|undefined}
   */
  getMonsterPlacement(placementId) {
    return this.monsterPlacements.find((p) => p.id === placementId);
  }

  /**
   * Get player character by ID
   * @param {string} characterId
   * @returns {Character|undefined}
   */
  getPlayerCharacter(characterId) {
    return this.playerCharacters.find((c) => c.id === characterId);
  }

  /**
   * Check if a cell is visible
   * @param {number} col
   * @param {number} row
   * @returns {boolean}
   */
  isCellVisible(col, row) {
    if (!this.mapVisibility) return true; // No visibility = everything visible
    return this.mapVisibility.gridCells.some((cell) => cell.col === col && cell.row === row);
  }

  /**
   * Get cell illumination
   * @param {number} col
   * @param {number} row
   * @returns {'bright'|'dim'|'none'}
   */
  getCellIllumination(col, row) {
    if (!this.mapVisibility) return 'bright';
    const cell = this.mapVisibility.gridCells.find((c) => c.col === col && c.row === row);
    return /** @type {'bright'|'dim'|'none'} */ (cell?.illumination || 'none');
  }

  /**
   * Reset all map state
   */
  reset() {
    this.walls = [];
    this.monsterPlacements = [];
    this.lights = [];
    this.openings = [];
    this.covers = [];
    this.pcStartingPositions = [];
    this.mapId = null;
    this.imageUploadId = null;
    this.backgroundUrl = '';
    this.gridSize = 140;
    this.gridOffsetX = 0;
    this.gridOffsetY = 0;
    this.backgroundColor = '#1a1a1a';
    this.gridRows = 0;
    this.gridCols = 0;
    this.gamePlayers = [];
    this.playerCharacters = [];
    this.invitedUsers = [];
    this.mapVisibility = null;
    this.combatState = {
      isActive: false,
      participants: [],
      currentTurnIndex: null,
      currentRound: 1,
    };
    this.turnState = null;
    this.hook = null;
    this.objective = null;
    this.adventureCompleted = false;
    this.partyWiped = false;
    this.globalIllumination = 'bright';
    this.waitingRoom = null;
    this.seed = null;
    this.notify();
  }
}

// Singleton instance
export const mapState = new MapStateManager();
